import React from 'react';
import { Box, Container, Typography, Button, IconButton, Tooltip, ButtonBase } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';

const slides = [
  { title: 'Custom AI Solutions', subtitle: 'Tailored Machine Learning for your products', image: '/custom-ai.png' },
  { title: 'Cloud Services', subtitle: 'Scalable cloud infrastructure and migration', image: '/cloud-services.png' },
  { title: 'AI Software Development', subtitle: 'Full-stack AI product development', image: '/AI-Software.png' },
  { title: 'Predictive Analytics', subtitle: 'Forecasting & insights to drive decisions', image: '/predictive-analytics.png' },
  { title: 'Digital Marketing', subtitle: 'Data-driven growth and engagement', image: '/digital-marketing.png' },
  { title: 'Big on Data Science & AI', subtitle: 'Empowering Your Business with Data Science', image: 'https://images.unsplash.com/photo-1506765515384-028b60a970df?w=1600&q=80&auto=format&fit=crop' }
];

export default function HeroSlider() {
  const swiperRef = React.useRef(null);
  const containerRef = React.useRef();
  const [isPaused, setIsPaused] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const [announce, setAnnounce] = React.useState('');

  React.useEffect(() => {
    if (isPaused) return undefined;
    const id = setInterval(() => {
      if (swiperRef.current) swiperRef.current.slideNext();
    }, 5000);
    return () => clearInterval(id);
  }, [isPaused]);

  React.useEffect(() => {
    setAnnounce(`${slides[index].title}`);
  }, [index]);

  return (
    <Box sx={{ position: 'relative', minHeight: { xs: 420, md: 520 } }}>
      <Box
        ref={containerRef}
        tabIndex={0}
        onFocus={() => setIsPaused(true)}
        onBlur={() => setIsPaused(false)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') swiperRef.current && swiperRef.current.slidePrev();
          if (e.key === 'ArrowRight') swiperRef.current && swiperRef.current.slideNext();
        }}
        role="region"
        aria-roledescription="carousel"
        aria-label="Hero slider"
        sx={{ outline: 'none' }}
      >
        <Swiper slidesPerView={1} onSwiper={s => (swiperRef.current = s)} onSlideChange={(s) => setIndex(s.activeIndex)}>
          {slides.map((s, i) => (
            <SwiperSlide key={i}>
              <Box
                sx={(theme) => ({
                  backgroundImage: `linear-gradient(rgba(75,85,89,0.62), rgba(75,85,89,0.62)), url(${s.image})`,
                  backgroundColor: s.image ? undefined : '#6b7176',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  color: theme.palette.common.white,
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: { xs: 420, md: 520 }
                })}
              >
                <Container sx={{ maxWidth: { md: 720 }, pl: { xs: 3, md: 10 }, pr: { xs: 3, md: 4 }, py: { xs: 6, md: 10 } }}>
                  <Typography
                    component="h1"
                    sx={(theme) => ({
                      fontWeight: 800,
                      color: theme.palette.common.white,
                      fontSize: { xs: '2rem', md: '3.5rem', lg: '4.75rem' },
                      lineHeight: 1.02,
                      letterSpacing: '-0.02em',
                      maxWidth: { md: '680px' }
                    })}
                  >
                    {s.title}
                  </Typography>

                  <Typography sx={{ mt: 2, opacity: 0.95, fontSize: { xs: '1rem', md: '1.125rem' } }}>{s.subtitle}</Typography>

                  <Box sx={{ mt: 4 }}>
                    <Button
                      variant="contained"
                      onClick={() => window.location.href = '/contact'}
                      sx={(theme) => ({
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.common.white,
                        borderRadius: 1,
                        px: 3,
                        py: 1.25,
                        textTransform: 'uppercase',
                        boxShadow: `0 6px 14px ${alpha(theme.palette.primary.main, 0.22)}`,
                        '&:hover': { bgcolor: theme.palette.primary.dark }
                      })}
                    >
                      Contact Us
                    </Button>
                  </Box>
                </Container>
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>

        <Tooltip title="Previous slide">
          <IconButton aria-label="Previous slide" onClick={() => { swiperRef.current && swiperRef.current.slidePrev(); setIsPaused(true); }} sx={(theme) => ({ bgcolor: alpha(theme.palette.common.black, 0.45), color: 'white', '&:focus-visible': { boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.24)}` } })}>
            <ArrowBackIosNewIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Next slide">
          <IconButton aria-label="Next slide" onClick={() => { swiperRef.current && swiperRef.current.slideNext(); setIsPaused(true); }} sx={(theme) => ({ bgcolor: alpha(theme.palette.common.black, 0.45), color: 'white', '&:focus-visible': { boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.24)}` } })}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Tooltip>

        {/* Indicators */}
        <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
          {slides.map((s, i) => (
            <ButtonBase
              key={i}
              onClick={() => { swiperRef.current && swiperRef.current.slideTo(i); setIndex(i); }}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={index === i ? 'true' : undefined}
              sx={(theme) => ({ width: 12, height: 12, borderRadius: '50%', bgcolor: index === i ? theme.palette.common.white : alpha(theme.palette.common.white, 0.5), '&:focus-visible': { boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.32)}` } })}
            />
          ))}
        </Box>

        <Box component="div" aria-live="polite" aria-atomic="true" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)', whiteSpace: 'nowrap' }}>{announce}</Box>
      </Box>
    </Box>
  );
}
